"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Compass, Leaf, ArrowRight, BookOpen, ShieldCheck, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
interface PillarItem {
  id: string;
  title: string;
  subtitle: string;
  icon: typeof Compass;
  badge: string;
  description: string;
  bullet1: string;
  bullet2: string;
}
const pillarsData: PillarItem[] = [{
  id: "dia-ly-phong-thuy",
  title: "Khoa học Địa lý Phong thủy",
  subtitle: "Nghiên cứu học thuật và chuẩn hóa lý luận",
  icon: Compass,
  badge: "Học thuật & Địa lý cổ truyền",
  description: "Tập trung nghiên cứu các nguyên lý địa lý cảnh quan, không gian sinh thái truyền thống và hệ giá trị phong thủy dưới góc nhìn khoa học thực nghiệm. Hội thực hiện khảo cứu thực địa, biên soạn tài liệu chuyên khảo và tổ chức thẩm định các mô hình quy hoạch không gian hài hòa với tự nhiên.",
  bullet1: "Khảo sát và chuẩn hóa thuật ngữ khoa học địa lý phong thủy",
  bullet2: "Ứng dụng bố cục cảnh quan trong xây dựng nông thôn và đô thị"
}, {
  id: "moi-truong-phat-trien",
  title: "Môi trường & Phát triển bền vững",
  subtitle: "Thích ứng khí hậu và kinh tế tuần hoàn",
  icon: Leaf,
  badge: "Ứng dụng sinh thái & Xã hội",
  description: "Triển khai các chương trình quan trắc địa lý môi trường, đánh giá biến động tài nguyên và đề xuất giải pháp thích ứng biến đổi khí hậu tại các vùng trọng điểm. Hội đồng hành cùng chính quyền và cộng đồng trong giảm phát thải khí nhà kính, phục hồi rừng đầu nguồn và phát triển sinh kế xanh.",
  bullet1: "Đánh giá tác động môi trường và chuyển đổi năng lượng sạch",
  bullet2: "Bảo tồn đa dạng sinh học và quản trị tài nguyên nước lưu vực"
}];
export default function Pillars() {
  const shouldReduceMotion = useReducedMotion();
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: (index: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.38,
        ease: [0.16, 1, 0.3, 1] as const,
        delay: shouldReduceMotion ? 0 : index * 0.08
      }
    })
  };
  return <section id="hai-mảng-hoạt-động-chính" data-nav="light" className="relative bg-background py-16 md:py-20 lg:py-24 border-b border-border">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
        {/* Section Header */}
        <div className="flex flex-col items-center text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-accent text-accent-foreground text-xs md:text-sm font-semibold tracking-wider uppercase mb-4 border border-border">
            <BookOpen className="size-3.5" />
            <span>Nhiệm vụ trọng tâm</span>
          </div>

          <h2 className="font-default text-2xl sm:text-3xl md:text-4xl font-semibold text-secondary tracking-tight max-w-2xl">
            Hai mảng hoạt động chính
          </h2>

          <div className="flex items-center justify-center gap-1.5 mt-3 mb-4">
            <span className="h-1 w-12 bg-primary rounded-full" />
            <span className="h-1 w-3 bg-secondary rounded-full" />
          </div>

          <p className="text-muted-foreground text-base md:text-lg max-w-3xl leading-relaxed">
            Hội Địa lý môi trường Việt Nam kết hợp nền tảng tri thức địa lý cổ truyền với khoa học môi trường hiện đại, kiến tạo giải pháp toàn diện cho sự phát triển bền vững quốc gia.
          </p>
        </div>

        {/* Dual Pillar Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 items-stretch">
          {pillarsData.map((pillar, i) => {
          const IconComponent = pillar.icon;
          return <motion.div key={pillar.id} data-index={i} custom={i} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.2
          }} variants={cardVariants} className="h-full">
                <Card className="h-full border border-border rounded-sm bg-card hover:border-primary transition-[border-color,box-shadow] shadow-sm hover:shadow-md relative overflow-hidden flex flex-col justify-between">
                  {/* Institutional Top Edge Rule */}
                  <div className="h-1 w-full bg-primary absolute top-0 left-0" />

                  <CardHeader className="pt-8 pb-4">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="size-12 rounded-sm bg-accent flex items-center justify-center border border-border shrink-0">
                        <IconComponent className="size-6 text-primary" strokeWidth={1.75} />
                      </div>
                      <Badge variant="outline" className="bg-muted text-muted-foreground border-border text-xs px-2.5 py-1 font-medium">
                        {pillar.badge}
                      </Badge>
                    </div>

                    <CardTitle className="text-xl md:text-2xl font-semibold text-foreground font-default leading-snug">
                      {pillar.title}
                    </CardTitle>
                    <p className="text-sm text-secondary font-medium mt-1">
                      {pillar.subtitle}
                    </p>
                  </CardHeader>

                  <CardContent className="space-y-6 pb-8 flex-1 flex flex-col justify-between">
                    <p className="text-foreground/90 text-sm md:text-base leading-relaxed">
                      {pillar.description}
                    </p>

                    <div className="pt-4 border-t border-border/60 space-y-2.5">
                      <div className="flex items-start gap-2.5 text-xs md:text-sm text-muted-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{pillar.bullet1}</span>
                      </div>
                      <div className="flex items-start gap-2.5 text-xs md:text-sm text-muted-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{pillar.bullet2}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Central Institutional CTA */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.35,
        delay: shouldReduceMotion ? 0 : 0.2
      }} className="mt-12 md:mt-16 flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
          <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium border-primary text-primary hover:bg-accent hover:text-accent-foreground px-8 py-3 rounded-sm text-base transition-colors">
            <Link to="/BusinessAreasPage">
              <span>Xem chi tiết lĩnh vực hoạt động</span>
              <ArrowRight className="size-4 ml-1.5" />
            </Link>
          </Button>

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="size-4 text-primary" />
            <span>Theo Điều lệ phê duyệt tại Quyết định 1157/QĐ-BNV</span>
          </div>
        </motion.div>
      </div>
    </section>;
}